/**
 * Role-fit score for BAILEY outreach.
 *
 * The score is intentionally signal-based rather than title-based: related job
 * titles should land in the same range even when employers phrase them
 * differently. Functional relevance is established first, then seniority is a
 * modifier. Negative functions (finance, legal, HR, procurement, technical,
 * etc.) remain low-fit even if they contain a generic word such as "event" or
 * "commercial".
 */
export function contactRelevance(title) {
    const t = String(title || '')
        .toLowerCase()
        .replace(/[&/+,_–—-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    // Functions that are not normal BAILEY outreach targets. These take
    // precedence over incidental overlap such as "Event Procurement Manager".
    const negativeFunction = /\bfinance\b|financial control|accounting|accounts payable|treasury|\btax\b|\baudit\b|\blegal\b|\bcounsel\b|compliance|\brisk\b|procurement|purchasing|human resources|\bhr\b|people (?:director|manager|partner|operations)|talent acquisition|recruit|information technology|\bit\b|engineering|developer|software|data science|cyber|security|facilities/.test(t);
    const vehicleTechnical = /\b(?:mechanical|aerodynamic|aero|vehicle|automotive|chassis|powertrain|engine|suspension|carbon fibre|composites?|industrial|product engineering|technical design|cad|r&d|research and development)\b/.test(t)
        && /design|designer|engineering|development|projects?|technical/.test(t)
        && !/\b(?:brand|marketing|campaign|communications|advertising|graphics?|visual|livery|motorsport marketing)\b/.test(t);
    if (negativeFunction || vehicleTechnical) {
        let low = 8;
        if (/\bhead\b|\bdirector\b|vice president|\bvp\b|\bchief\b|\bcmo\b|\bcco\b/.test(t))
            low += 5;
        else if (/\bmanager\b|\blead\b/.test(t))
            low += 3;
        return Math.min(25, low);
    }
    // Semantic role families. These are deliberately broader than one exact
    // title so "Fan Engagement Manager", "Brand Experience Lead" and
    // "Manager, Events and Fan Experience" are treated as neighbouring roles.
    const partnership = /\bpartnerships?\b|\bsponsorships?\b|\balliances?\b|partner marketing|commercial partnerships?/.test(t);
    const experience = /\bexperiential\b|brand experience|fan experience|fan engagement|supporter experience|supporter engagement|guest experience|live experience|event experience|audience experience|visitor experience/.test(t);
    const activation = /\bactivation\b|\bactivations\b|partnership activation|sponsorship activation|brand activation|fan activation/.test(t);
    const events = /event marketing|event production|event operations|event delivery|event management|live events?|\bevents?\b/.test(t);
    const hospitality = /\bhospitality\b|vip experience|premium experience|guest programme|guest program/.test(t);
    const creative = /\bcreative\b|\bdesign\b|art director|designer|creative producer|\bstudio\b|brand (?:director|manager|lead|strategy|identity|creative)|(?:head|director|manager|lead|vp|vice president|chief) (?:of )?brand\b|chief brand officer|\bcbo\b/.test(t);
    const marketing = /\bmarketing\b|\bcampaigns?\b|content marketing|\bsocial\b|product marketing|growth marketing|digital marketing|performance marketing|paid media|media planning|customer acquisition|lifecycle|crm marketing|chief marketing officer|\bcmo\b/.test(t);
    // PR / communications is adjacent to marketing, but it is not normally a core BAILEY buying function.
    // Keep pure comms/PR materially below Marketing; mixed titles can still rise when they contain a genuine buying signal.
    const communications = /public relations|media relations|press office|press relations|publicity|corporate communications|\bcommunications?\b|\bcomms\b|\bpr\b/.test(t);
    const familyScores = [
        partnership ? 66 : 0,
        experience ? 68 : 0,
        activation ? 68 : 0,
        events ? 64 : 0,
        hospitality ? 60 : 0,
        creative ? 60 : 0,
        marketing ? 58 : 0,
        communications ? 32 : 0,
    ];
    const matchedFamilies = familyScores.filter(Boolean).length;
    const functionalBase = Math.max(...familyScores);
    let score;
    if (functionalBase > 0)
        score = functionalBase;
    else if (/business development|\bcommercial\b|\bsales\b|\brevenue\b/.test(t))
        score = 34;
    else
        score = 20;
    // A role spanning two complementary outreach functions gets a small lift,
    // but overlapping keywords never stack without limit.
    if (matchedFamilies >= 2)
        score += 4;
    const executiveSenior = /\bhead\b|\bdirector\b|vice president|\bvp\b|\bchief\b|\bcmo\b|\bcco\b/.test(t);
    const seniorManager = /\bsenior\s+(?:manager|lead)\b/.test(t);
    const managerLead = /\bmanager\b|\blead\b/.test(t);
    if (functionalBase > 0) {
        const communicationsOnly = communications && !(partnership || experience || activation || events || hospitality || creative || marketing);
        if (executiveSenior) {
            score += communicationsOnly ? 10 : 18;
            // Senior owners of BAILEY's core buying functions are decision-makers.
            // Pure PR/comms seniority does not inherit the Marketing decision-maker floor.
            const decisionMakerFloor = communicationsOnly ? 0 :
                partnership || experience || activation ? 92 :
                    marketing || creative || events ? 88 :
                        hospitality ? 84 : 0;
            score = Math.max(score, decisionMakerFloor);
        }
        else if (seniorManager)
            score += communicationsOnly ? 8 : 15;
        else if (managerLead)
            score += communicationsOnly ? 7 : 12;
    }
    else {
        // Seniority alone is not a reason to contact someone.
        if (executiveSenior)
            score += 5;
        else if (seniorManager || managerLead)
            score += 3;
    }
    // Event-adjacent commercial roles are useful context but are not equivalent
    // to owning fan experience, activation or event delivery.
    const commercialDelivery = /\bsales\b|\brevenue\b|\bticketing\b|box office|account executive|business development/.test(t);
    const strategicEventSignal = experience || activation || partnership || marketing || creative;
    if (events && commercialDelivery && !strategicEventSignal)
        score -= 20;
    // Motorsport in the actual job title is a useful relevance signal for BAILEY's
    // sports outreach, but it should never override a clearly irrelevant function.
    // Negative-function roles already return above, so the bonus only strengthens
    // otherwise viable roles.
    if (/\bmotorsport\b/.test(t))
        score += 8;
    if (/assistant|coordinator|intern|graduate|apprentice/.test(t))
        score -= 18;
    return Math.max(0, Math.min(100, score));
}
export function rankCaseStudies(contact, opportunity, library) {
    const role = contact.title.toLowerCase();
    return library.map(item => {
        let score = 0;
        const reasons = [];
        for (const tag of opportunity.serviceTags ?? []) {
            if (item.tags.includes(tag)) {
                score += 5;
                reasons.push(tag);
            }
        }
        if (opportunity.sport && item.sports.includes(opportunity.sport)) {
            score += 7;
            reasons.push(opportunity.sport);
        }
        if (/partnership|sponsor/.test(role) && item.tags.includes('Partnership Launch'))
            score += 6;
        if (/experiential|experience|hospitality|event/.test(role) && item.tags.includes('Experiential'))
            score += 6;
        if (/creative|design|marketing|campaign/.test(role) && item.tags.includes('Campaign / KV'))
            score += 5;
        if (/motion|content/.test(role) && item.tags.includes('Motion'))
            score += 4;
        return { ...item, score, reasons };
    }).sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}
